/**
 * Golden particle system - lightweight canvas embers drifting upward.
 * Auto-disables on mobile and prefers-reduced-motion.
 * Handles tab visibility changes gracefully.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  decay: number;
}

export function initParticles(canvas: HTMLCanvasElement): () => void {
  if (window.innerWidth < 768) return () => {};

  const ctxMaybe = canvas.getContext('2d');
  if (!ctxMaybe) return () => {};
  const ctx: CanvasRenderingContext2D = ctxMaybe;

  let raf = 0;
  let running = true;
  let lastTime = 0;
  const particles: Particle[] = [];
  const MAX = 60;

  function resize() {
    const dpr = devicePixelRatio;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function spawn() {
    if (particles.length >= MAX) return;
    particles.push({
      x: Math.random() * window.innerWidth,
      y: window.innerHeight + 10,
      vx: (Math.random() - 0.5) * 0.3,
      vy: -(0.3 + Math.random() * 0.7),
      size: 1 + Math.random() * 2.5,
      alpha: 0.4 + Math.random() * 0.5,
      decay: 0.001 + Math.random() * 0.003,
    });
  }

  function tick(time: number) {
    if (!running) return;
    if (document.hidden) {
      raf = requestAnimationFrame(tick);
      return;
    }

    const dt = lastTime ? Math.min(time - lastTime, 32) : 16;
    lastTime = time;
    const factor = dt / 16;

    const w = window.innerWidth;
    const h = window.innerHeight;
    ctx.clearRect(0, 0, w, h);

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      if (!p) continue;
      p.x += p.vx * factor;
      p.y += p.vy * factor;
      p.alpha -= p.decay * factor;
      p.vx += (Math.random() - 0.5) * 0.02;

      if (p.alpha <= 0 || p.y < -10) {
        particles.splice(i, 1);
        continue;
      }

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(201, 168, 76, ${p.alpha})`;
      ctx.fill();

      if (p.size > 1.5) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201, 168, 76, ${p.alpha * 0.15})`;
        ctx.fill();
      }
    }

    if (Math.random() < 0.3) spawn();
    raf = requestAnimationFrame(tick);
  }

  function onVisibilityChange() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
    } else {
      particles.length = 0;
      lastTime = 0;
      resize();
      raf = requestAnimationFrame(tick);
    }
  }

  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', onVisibilityChange);
  raf = requestAnimationFrame(tick);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
